import PropTypes from 'prop-types';
import secureLocalStorage from 'react-secure-storage';
import { useMemo, useState, useContext, createContext } from 'react';

// @mui
import { CssBaseline } from '@mui/material';
import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider as MUIThemeProvider,
} from '@mui/material/styles';

import shadows from './shadows';
import typography from './typography';
//
import { getPalette } from './palette';
import GlobalStyles from './globalStyles';
import customShadows from './customShadows';
import componentsOverride from './overrides';

// ----------------------------------------------------------------------

const ThemeModeContext = createContext();

// Export hook to allow any child component to toggle the theme mode
export const useThemeMode = () => useContext(ThemeModeContext);

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default function ThemeProvider({ children }) {
  // Read initial selection securely, fallback to light mode
  const [themeMode, setThemeMode] = useState(() => {
    const stored = secureLocalStorage.getItem('themeMode');
    return stored || 'light';
  });

  const toggleThemeMode = () => {
    setThemeMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      secureLocalStorage.setItem('themeMode', next);
      return next;
    });
  };

  const contextValue = useMemo(() => ({ themeMode, toggleThemeMode }), [themeMode]);

  const themeOptions = useMemo(
    () => ({
      palette: getPalette(themeMode),
      shape: { borderRadius: 12 },
      typography,
      shadows: shadows(),
      customShadows: customShadows(),
    }),
    [themeMode]
  );

  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <StyledEngineProvider injectFirst>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles />
          {children}
        </MUIThemeProvider>
      </StyledEngineProvider>
    </ThemeModeContext.Provider>
  );
}
